// ErrorBoundary.js — catches React render errors + logs them to Daphne.
//
// Wrap the whole app: <ErrorBoundary><App /></ErrorBoundary>.
//
// Without this, a runtime error in ANY component unmounts the whole tree and
// leaves a blank page (which is what studios reported on 2026-07-09 — no way
// to diagnose remotely because no console access on their devices). With
// this: the user sees a friendly "Something went wrong, click Retry" screen
// and we get a POST to /api/v1/gym_owner/client_error with the stack trace,
// browser, and URL. Grep daphne journal for "CLIENT_ERROR" to find it.
//
// Also installs a window.onerror + unhandledrejection listener so we catch
// non-React crashes (async fetch failures, event handlers) too.

import React from 'react';

const CLIENT_ERROR_URL = 'https://gym.fusionhub.studio/api/v1/gym_owner/client_error';
let _lastPostAt = 0;

function postClientError(payload) {
  // De-bounce: never fire more than 1 per 3s so a runaway loop can't DDoS us.
  const now = Date.now();
  if (now - _lastPostAt < 3000) return;
  _lastPostAt = now;

  const body = JSON.stringify({
    message: String(payload.message || '').slice(0, 500),
    stack: String(payload.stack || '').slice(0, 4000),
    userAgent: navigator.userAgent || '',
    url: window.location.href,
    tenantSlug: (() => {
      try {
        const u = JSON.parse(localStorage.getItem('userData') || '{}');
        return u.tenantSlug || u.franchise_id || '';
      } catch { return ''; }
    })(),
    extra: payload.extra || '',
  });
  // sendBeacon is fire-and-forget and survives page unload; fetch is fallback.
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(CLIENT_ERROR_URL, new Blob([body], { type: 'application/json' }));
      return;
    }
  } catch {}
  try {
    fetch(CLIENT_ERROR_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true,
    }).catch(() => {});
  } catch {}
}

// Global handlers — catch things React can't (async errors, event handlers).
if (typeof window !== 'undefined' && !window.__fh_errorHooked) {
  window.__fh_errorHooked = true;
  window.addEventListener('error', (e) => {
    postClientError({
      message: 'window.onerror: ' + (e.message || ''),
      stack: (e.error && e.error.stack) || '',
      extra: (e.filename || '') + ':' + (e.lineno || '') + ':' + (e.colno || ''),
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    postClientError({
      message: 'unhandledrejection: ' + (e.reason && e.reason.message ? e.reason.message : String(e.reason)),
      stack: (e.reason && e.reason.stack) || '',
    });
  });
}

export default class ErrorBoundary extends React.Component {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(err) {
    return { hasError: true, message: err && err.message ? err.message : String(err) };
  }

  componentDidCatch(err, info) {
    postClientError({
      message: 'React: ' + (err && err.message ? err.message : String(err)),
      stack: (err && err.stack) || '',
      extra: (info && info.componentStack) ? info.componentStack.slice(0, 400) : '',
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // silent mode — used for inner boundaries around specific widgets (Header,
    // notifications). Log the crash to /client_error but render either the
    // caller-supplied fallback OR nothing, so the rest of the page keeps
    // working. Studio never sees the full-page retry screen just because
    // their nav crashed. A fallback prop lets us keep basic UI (logo, logout)
    // even when the fancy Header component tears itself down.
    if (this.props.silent) return this.props.fallback || null;

    // Friendly retry screen. Studios see this instead of a blank page.
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0a0a0a', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif',
        padding: 24,
      }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28, background: '#FCDC08',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#0a0a0a', fontSize: 32, fontWeight: 900, marginBottom: 20,
          }}>!</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>
            Something went wrong
          </h1>
          <p style={{ color: '#a0a0a0', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
            The dashboard hit an unexpected error and stopped rendering. Your data is safe.
            Please try reloading the page. If it happens again, the error has been sent to
            our support team automatically.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 32px', borderRadius: 8, background: '#FCDC08', color: '#0a0a0a',
              border: 'none', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>
            Reload dashboard
          </button>
          {this.state.message && (
            <div style={{
              marginTop: 24, padding: 12, background: '#1a1a1a', borderRadius: 6,
              fontSize: 11, fontFamily: 'monospace', color: '#888', wordBreak: 'break-all',
              textAlign: 'left',
            }}>
              {this.state.message}
            </div>
          )}
        </div>
      </div>
    );
  }
}
